/**
 * Inverse-design / optimisation SECTION (FXC-9009 · DESIGN §8) — the host wiring that joins the
 * NN-Newton spine (`design-opt/`) to the workbench. It composes the three presentational pieces:
 *
 *   1. `RangeControls`   — per-DOF min/max + lock editor over the hook's `ranges` sidecar (the
 *                          MSES-style mixed-inverse: locked DOFs are pinned). An "add DOF" affordance
 *                          seeds a box around the live value of any not-yet-ranged flat axis.
 *   2. `CpPlot`          — the surface-Cp strip (`surfaceCp`, honest-absent without an NN) with an
 *                          editable target-Cp overlay + a draggable mixed-inverse [x0,x1] region band.
 *   3. `InverseDesignPanel` — the driver: Solve (target-Cp inverse design) + Optimize (scalar
 *                          objective), with a convergence sparkline + residual read-out.
 *
 * This component owns ONLY the ephemeral driver state (history / residual / solving / target / region /
 * objective). Every persistent edit is delegated to the `useFoilDesign` hook via the callbacks passed
 * in, so the section never duplicates design state. Without a bound NN the panel still renders — Solve /
 * Optimize report `applied:false` (the hook no-ops), and `CpPlot` shows its empty state — so the wiring
 * is visible and honest before the runtime worker-loaded model arrives (the `setNN` seam).
 */
import { useCallback, useMemo, useState } from "react";

import { flattenDesign, type HighLiftAirfoil } from "../geometry";
import type {
  FoilObjectiveKind,
  GapTargetResult,
  InverseDesignResult,
  OptimizeResult,
  ParamRange,
  RangeMap,
  RunInverseDesignOpts,
  RunOptimizeOpts,
} from "./useFoilDesign";
import type { SurfaceCp } from "../design-opt/metrics";
import type { SurfaceCpElement } from "./FlowVisLayer";
import { CpPlot, type CpTarget } from "./CpPlot";
import { InverseDesignPanel, type OptimizeObjective } from "./InverseDesignPanel";
import { RangeControls, type RangePatch, type RangeSpec } from "./RangeControls";
import { useHighliftTheme } from "./useHighliftTheme";

const FONT_STACK = "'TWK Everett', 'Outfit', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif";
const MONO_STACK = "'JetBrains Mono', 'SFMono-Regular', Menlo, monospace";

/** Map the panel's scalar objective vocabulary → the hook's scalar objective kinds. */
const OBJECTIVE_MAP: Record<Exclude<OptimizeObjective, "match-cp">, FoilObjectiveKind> = {
  clmax: "maxCL",
  "cd-at-cl": "minCD",
};

export interface InverseDesignSectionProps {
  /** The live design — supplies the flat-axis live values for the range rows + the add-DOF menu. */
  config: HighLiftAirfoil;
  /** The hook's range sidecar (flat-axis key → box). */
  ranges: RangeMap;
  /** Set / replace one DOF's box. */
  setRange: (key: string, range: ParamRange) => void;
  /** Drop a DOF from the ranges (it stops participating in the loop). */
  clearRange: (key: string) => void;
  /** Predicted surface-Cp strips (honest-null without a bound NN). */
  surfaceCp: SurfaceCpElement[] | null;
  /** True once an NN is bound (drives the no-NN hint). */
  hasNN: boolean;
  /** Run the target-Cp inverse design over the unlocked ranged DOFs. */
  runInverseDesign: (target: SurfaceCp, opts?: RunInverseDesignOpts) => Promise<InverseDesignResult>;
  /** Run a scalar-objective optimise over the unlocked ranged DOFs. */
  runOptimize: (objective: FoilObjectiveKind, opts?: RunOptimizeOpts) => Promise<OptimizeResult>;
  /** Inverse-design a gap-flow slot metric to a target value over the free DOFs (pure geometry, #10). */
  runGapTarget: (metricKey: string, targetValue: number) => Promise<GapTargetResult>;
  /** Live flattened gap-flow scalar outputs (slot_*_gap, …) — builds the target-metric picker. */
  gapMetricOutputs: Record<string, number>;
}

/** Humanise a gap-flow output key (`slot_vf_gap` → `vane→flap gap`) for the metric picker. */
function gapMetricLabel(key: string): string {
  const m = /^slot_([a-z0-9]+)_(.+)$/.exec(key);
  if (!m) return key.replace(/_/g, " ");
  const slot = m[1] === "mv" ? "main→vane" : m[1] === "vf" ? "vane→flap" : `slot ${m[1]}`;
  return `${slot} ${m[2]!.replace(/_/g, " ")}`;
}

/** Humanise a flat-axis key for the range-row / menu label (`vane_te_z` → `vane te z`). */
function humanise(key: string): string {
  return key.replace(/_/g, " ");
}

/** A symmetric default box around a live value (≥0.05 wide, or 30% of |value| for large axes). */
function defaultBox(value: number): ParamRange {
  const half = Math.max(0.05, Math.abs(value) * 0.3);
  return { min: value - half, max: value + half };
}

/** Convert a `CpTarget` (either side optional) → the solver's `SurfaceCp` (both sides required). */
function targetToSurfaceCp(t: CpTarget): SurfaceCp {
  return { upper: t.upper ?? [], lower: t.lower ?? [] };
}

/** Convert a solved `SurfaceCpElement` → an editable `CpTarget` (a deep copy so drags don't mutate). */
function stripToTarget(strip: SurfaceCpElement): CpTarget {
  return {
    element: strip.element,
    upper: strip.upper.map((p) => [p[0], p[1]] as [number, number]),
    lower: strip.lower.map((p) => [p[0], p[1]] as [number, number]),
  };
}

export function InverseDesignSection({
  config,
  ranges,
  setRange,
  clearRange,
  surfaceCp,
  hasNN,
  runInverseDesign,
  runOptimize,
  runGapTarget,
  gapMetricOutputs,
}: InverseDesignSectionProps) {
  const palette = useHighliftTheme();

  // --- driver state (ephemeral; the persistent design lives in the hook) -----------------
  const [history, setHistory] = useState<number[]>([]);
  const [residual, setResidual] = useState<number | null>(null);
  const [solving, setSolving] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [objective, setObjective] = useState<OptimizeObjective>("clmax");
  const [region, setRegion] = useState<[number, number] | undefined>(undefined);
  const [target, setTarget] = useState<CpTarget | undefined>(undefined);

  // Gap-flow target picker (FXC-9009 #10): the targetable slot metrics (gap / overlap / convergence)
  // pulled live from the gap-flow outputs, plus the selected metric + its target value.
  const gapMetricKeys = useMemo(
    () =>
      Object.keys(gapMetricOutputs)
        .filter((k) => /_(gap|overlap|convergence)$/.test(k))
        .sort()
        .map((key) => ({ key, label: gapMetricLabel(key) })),
    [gapMetricOutputs],
  );
  const [gapMetric, setGapMetric] = useState<string>("");
  // Seed the target from the first metric's live value (gap / overlap / convergence live on very
  // different scales, so a fixed default would mislead — e.g. convergence ~1, gap ~0.01).
  const [gapTarget, setGapTarget] = useState<number>(() => {
    const k = gapMetricKeys[0]?.key;
    const v = k ? gapMetricOutputs[k] : undefined;
    return typeof v === "number" && Number.isFinite(v) ? Number(v.toFixed(4)) : 0.02;
  });
  const activeGapMetric = gapMetric || gapMetricKeys[0]?.key || "";
  // Switching the metric re-seeds the target to that metric's current value (same scale).
  const onGapMetricChange = useCallback(
    (key: string) => {
      setGapMetric(key);
      const v = gapMetricOutputs[key];
      if (typeof v === "number" && Number.isFinite(v)) setGapTarget(Number(v.toFixed(4)));
    },
    [gapMetricOutputs],
  );

  // The live flat-axis values (for the range rows + the add-DOF menu options).
  const flat = useMemo(() => flattenDesign(config), [config]);

  // Range rows: one per ranged DOF, value pulled live from the design. Keys with no live value
  // (an axis the current config doesn't emit) are still shown at their stored box so they can be
  // cleared. Sorted for a stable order.
  const rangeSpecs = useMemo<RangeSpec[]>(() => {
    return Object.entries(ranges)
      .map(([key, r]) => ({
        key,
        label: humanise(key),
        value: typeof flat[key] === "number" ? flat[key]! : NaN,
        min: r.min,
        max: r.max,
        locked: r.locked,
      }))
      .sort((a, b) => a.key.localeCompare(b.key));
  }, [ranges, flat]);

  // Flat axes not yet ranged — the add-DOF menu. Sorted; shape DOFs first is unnecessary (the user
  // picks), but a stable alphabetical order keeps the menu predictable.
  const addableKeys = useMemo(
    () => Object.keys(flat).filter((k) => !(k in ranges)).sort((a, b) => a.localeCompare(b)),
    [flat, ranges],
  );

  const onRangeChange = useCallback(
    (key: string, patch: RangePatch) => setRange(key, { min: patch.min, max: patch.max, locked: patch.locked }),
    [setRange],
  );

  const addDof = useCallback(
    (key: string) => {
      const value = flat[key];
      if (typeof value !== "number" || !Number.isFinite(value)) return;
      setRange(key, defaultBox(value));
    },
    [flat, setRange],
  );

  // The targeted element's strip (default: the first solved strip — the main). Used to seed the
  // editable target and as the Solve fallback when the user hasn't reshaped a target yet.
  const firstStrip = surfaceCp && surfaceCp.length > 0 ? surfaceCp[0]! : null;

  const seedTarget = useCallback(() => {
    if (!firstStrip) {
      setNote("Bind an NN to read a Cp strip before seeding a target.");
      return;
    }
    setTarget(stripToTarget(firstStrip));
    setNote(`target seeded from “${firstStrip.element}” — drag points to reshape it`);
  }, [firstStrip]);

  const onSolve = useCallback(() => {
    const t = target ?? (firstStrip ? stripToTarget(firstStrip) : null);
    if (!t) {
      setNote("No target Cp yet — bind an NN and seed a target first.");
      return;
    }
    const ranged = Object.keys(ranges).length;
    if (ranged === 0) {
      setNote("Add at least one ranged DOF for the solver to move.");
      return;
    }
    setSolving(true);
    setNote(null);
    void runInverseDesign(targetToSurfaceCp(t), { element: t.element, region })
      .then((res) => {
        setHistory(res.history);
        setResidual(res.history.length > 0 ? res.history[res.history.length - 1]! : null);
        setNote(
          res.applied
            ? `solved — residual ${Number.isFinite(res.finalCost) ? res.finalCost.toExponential(2) : "—"} over ${res.history.length - 1} iters`
            : "no-op — bind an NN and add unlocked DOFs to solve",
        );
      })
      .finally(() => setSolving(false));
  }, [target, firstStrip, ranges, region, runInverseDesign]);

  const onOptimize = useCallback(() => {
    if (objective === "match-cp") {
      onSolve();
      return;
    }
    const ranged = Object.keys(ranges).length;
    if (ranged === 0) {
      setNote("Add at least one ranged DOF for the optimiser to move.");
      return;
    }
    setSolving(true);
    setNote(null);
    void runOptimize(OBJECTIVE_MAP[objective], { element: firstStrip?.element })
      .then((res) => {
        setHistory(res.history);
        setResidual(res.history.length > 0 ? res.history[res.history.length - 1]! : null);
        setNote(
          res.applied
            ? `optimised — objective ${Number.isFinite(res.finalValue) ? res.finalValue.toExponential(3) : "—"}`
            : "no-op — bind an NN and add unlocked DOFs to optimise",
        );
      })
      .finally(() => setSolving(false));
  }, [objective, onSolve, ranges, firstStrip, runOptimize]);

  const onGapSolve = useCallback(() => {
    const key = activeGapMetric;
    if (!key) {
      setNote("No gap-flow metric available to target.");
      return;
    }
    if (Object.keys(ranges).length === 0) {
      setNote("Add at least one ranged DOF for the gap-flow target to move.");
      return;
    }
    setSolving(true);
    setNote(null);
    void runGapTarget(key, gapTarget)
      .then((res) => {
        setHistory(res.history);
        setResidual(res.history.length > 0 ? res.history[res.history.length - 1]! : null);
        setNote(
          !res.applied
            ? "no-op — add at least one unlocked DOF to drive the gap."
            : !Number.isFinite(res.finalValue)
              ? "no signal — these DOFs don't move this slot at the current deploy; deploy the flap first."
              : `gap-flow ${gapMetricLabel(key)} → ${res.finalValue.toFixed(4)} (target ${gapTarget.toFixed(4)})`,
        );
      })
      .finally(() => setSolving(false));
  }, [activeGapMetric, ranges, gapTarget, runGapTarget]);

  const toggleRegion = useCallback(() => {
    setRegion((r) => (r ? undefined : [0.0, 0.3]));
  }, []);

  const labelCss: React.CSSProperties = { fontSize: 11, color: palette.ink3, fontFamily: FONT_STACK };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10, fontFamily: FONT_STACK }}>
      {!hasNN && (
        <div
          style={{
            fontSize: 11,
            lineHeight: 1.4,
            color: palette.ink3,
            fontFamily: FONT_STACK,
            border: `1px dashed ${palette.line}`,
            borderRadius: 6,
            padding: "7px 9px",
            background: palette.surface,
          }}
        >
          No flowfield-NN bound yet — Solve / Optimize will no-op and the Cp strip is empty. The loop
          lights up once the runtime worker-loaded model is bound (the <code>setNN</code> seam).
        </div>
      )}

      {/* Cp strip + editable target overlay + region band */}
      <CpPlot
        surfaceCp={surfaceCp ?? []}
        target={target}
        onTargetChange={setTarget}
        region={region}
        onRegionChange={setRegion}
      />

      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <button
          type="button"
          onClick={seedTarget}
          disabled={!firstStrip}
          style={{
            padding: "5px 12px",
            borderRadius: 6,
            fontSize: 12,
            fontFamily: FONT_STACK,
            cursor: firstStrip ? "pointer" : "default",
            border: `1px solid ${firstStrip ? palette.primary : palette.line}`,
            background: firstStrip ? palette.primarySoft : palette.surface,
            color: firstStrip ? palette.primary : palette.ink4,
          }}
          title="Copy the targeted element's current Cp into an editable target you can reshape"
        >
          Seed target from current
        </button>
        <label style={{ ...labelCss, display: "inline-flex", alignItems: "center", gap: 5 }}>
          <input type="checkbox" checked={Boolean(region)} onChange={toggleRegion} />
          mixed-inverse region
        </label>
      </div>

      {/* Add-DOF affordance — turn any live flat axis into an optimisation variable. */}
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <label style={labelCss}>add DOF</label>
        <select
          value=""
          onChange={(e) => {
            if (e.target.value) addDof(e.target.value);
          }}
          disabled={addableKeys.length === 0}
          style={{
            flex: 1,
            minWidth: 0,
            fontSize: 11,
            fontFamily: MONO_STACK,
            color: palette.ink,
            background: palette.surface,
            border: `1px solid ${palette.line}`,
            borderRadius: 4,
            padding: "3px 6px",
          }}
        >
          <option value="">{addableKeys.length === 0 ? "all axes ranged" : "choose a degree of freedom…"}</option>
          {addableKeys.map((k) => (
            <option key={k} value={k}>
              {humanise(k)}
            </option>
          ))}
        </select>
      </div>

      <RangeControls specs={rangeSpecs} onChange={onRangeChange} />
      {rangeSpecs.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
          {rangeSpecs.map((s) => (
            <button
              key={s.key}
              type="button"
              onClick={() => clearRange(s.key)}
              title={`Remove ${s.label} from the optimisation variables`}
              style={{
                fontSize: 10,
                fontFamily: MONO_STACK,
                cursor: "pointer",
                padding: "2px 7px",
                borderRadius: 999,
                border: `1px solid ${palette.line}`,
                background: palette.surface,
                color: palette.ink3,
              }}
            >
              {s.label} ×
            </button>
          ))}
        </div>
      )}

      <InverseDesignPanel
        history={history}
        residual={residual}
        solving={solving}
        onSolve={onSolve}
        region={region}
        onRegionChange={setRegion}
        objective={objective}
        onObjectiveChange={setObjective}
        onOptimize={onOptimize}
        gapMetricKeys={gapMetricKeys}
        gapMetric={activeGapMetric}
        onGapMetricChange={onGapMetricChange}
        gapTarget={gapTarget}
        onGapTargetChange={setGapTarget}
        onGapSolve={onGapSolve}
      />

      {note && (
        <div style={{ fontSize: 11, fontFamily: MONO_STACK, color: palette.ink3 }}>{note}</div>
      )}
    </div>
  );
}
